import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import { useCallback, useEffect, useRef } from "react";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import type { AssistantConversationStatus } from "../types";

/**
 * Clears a conversation's blue dot while the reader is actually looking at it.
 *
 * Two moments mark a thread read, and only two:
 *
 * 1. It becomes the open conversation.
 * 2. Its status **flips into** needs-you while it stays open — Claudia finished, or asked
 *    something, in front of someone who is already there.
 *
 * The second one is written as a transition, not a state, which is what keeps it from looping:
 * a thread parked on an unanswered question stays `needs_you` *after* `markThreadRead`, so a
 * "mark whenever needs-you" rule would re-fire on every re-render for as long as the question
 * stood. A steady status marks nothing; only the edge into it does.
 *
 * A hidden document defers rather than skips: the mark is queued and flushed on the next
 * `visibilitychange`, so a background tab does not quietly clear a dot the person never saw,
 * and coming back to the tab does not leave a stale one behind either.
 */
export function useMarkThreadRead({
  projectId,
  threadId,
  status,
  enabled = true,
}: {
  projectId: Id<"projects"> | null | undefined;
  threadId: string | null | undefined;
  status: AssistantConversationStatus | null | undefined;
  /**
   * Whether this mount is the one being read. A kept-alive background surface still renders and
   * still knows the open thread, but nobody is looking at it, so it must not clear a dot
   * (LAT-990). Defaults to true for callers that only mount while visible.
   */
  enabled?: boolean;
}): void {
  const markThreadRead = useObservedMutation(api.claudia.markThreadRead, {
    feature: "assistant",
    operation: "mark-thread-read",
  });

  const previousStatusRef = useRef<AssistantConversationStatus | null>(null);
  const markedThreadIdRef = useRef<string | null>(null);
  const pendingThreadIdRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  /** `flush` re-enters itself after a request settles, and a `useCallback` cannot name itself. */
  const flushRef = useRef<((targetThreadId: string) => void) | null>(null);

  const flush = useCallback(
    (targetThreadId: string) => {
      if (!projectId) {
        return;
      }
      // A mark asked for while one is in flight is *queued*, never dropped: the second one is
      // usually the one that matters (the thread switched, or its status just flipped), and
      // returning early here used to lose it outright — leaving a dot up on a conversation the
      // reader is looking at until they navigated away and back.
      if (inFlightRef.current) {
        pendingThreadIdRef.current = targetThreadId;
        return;
      }
      inFlightRef.current = true;
      pendingThreadIdRef.current = null;
      void markThreadRead({ projectId, threadId: targetThreadId })
        .catch(() => {
          // A read marker is a convenience, not a promise. A failed mark leaves the dot up,
          // which is the safe direction, and the next open tries again.
        })
        .finally(() => {
          inFlightRef.current = false;
          const queued = pendingThreadIdRef.current;
          // A hidden document keeps its deferral: the `visibilitychange` listener owns that one.
          if (
            queued &&
            (typeof document === "undefined" || document.visibilityState !== "hidden")
          ) {
            flushRef.current?.(queued);
          }
        });
    },
    [markThreadRead, projectId]
  );

  flushRef.current = flush;

  const request = useCallback(
    (targetThreadId: string) => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        pendingThreadIdRef.current = targetThreadId;
        return;
      }
      flush(targetThreadId);
    },
    [flush]
  );

  useEffect(() => {
    if (!projectId || !threadId || !enabled) {
      previousStatusRef.current = null;
      markedThreadIdRef.current = null;
      pendingThreadIdRef.current = null;
      return;
    }

    const isNewlyOpened = markedThreadIdRef.current !== threadId;
    const previousStatus = isNewlyOpened ? null : previousStatusRef.current;
    const flippedToNeedsYou = status === "needs_you" && previousStatus !== "needs_you";

    previousStatusRef.current = status ?? null;
    markedThreadIdRef.current = threadId;

    if (isNewlyOpened || flippedToNeedsYou) {
      request(threadId);
    }
  }, [enabled, projectId, request, status, threadId]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const onVisibilityChange = () => {
      const pending = pendingThreadIdRef.current;
      if (pending && document.visibilityState === "visible") {
        flush(pending);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [flush]);
}
